import type { FC, FormEvent, ReactNode } from 'react'
import type { AccommodationOption } from '@/types'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { CalendarDays, CheckCircle2, Info, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { nightsBetween } from '@/lib/format'
import { apiRequest, errorCode, errorFields } from '@/lib/client/api'
import { fill, plural, useI18n } from '@/i18n'
import { Button, buttonClass } from '@/components/ui/button'
import { CountrySelect } from '@/components/forms/country-select'

import {
    Checkbox,
    Field,
    Input,
    Select,
    Textarea
} from '@/components/ui/field'

export interface BookingPrefill {
    firstName: string
    lastName: string
    email: string
    phone: string
    nationality: string
}

export interface BookingInitial {
    accommodationId?: string
    destination?: string
    arrival?: string
    departure?: string
    adults?: number
    children?: number
}

interface BookingFormProps {
    accommodations: Array<AccommodationOption>
    locations: Array<string>
    today: string
    prefill: BookingPrefill | null
    initial?: BookingInitial
    lockedAccommodationId?: string
    compact?: boolean
}

interface FormState {
    destination: string
    accommodationId: string
    arrivalDate: string
    departureDate: string
    adults: string
    children: string
    firstName: string
    lastName: string
    email: string
    phone: string
    nationality: string
    specialRequests: string
    message: string
    privacy: boolean
    website: string
}

interface SuccessState {
    bookingNumber: string
    customerEmailSent: boolean
    email: string
}

const FLEXIBLE = 'flexible'
const ADVICE = 'advice'

const addDays = (date: string, days: number): string => {
    const value = new Date(`${date}T00:00:00Z`)

    value.setUTCDate(value.getUTCDate() + days)

    return value.toISOString().slice(0, 10)
}

export const BookingForm: FC<BookingFormProps> = ({
    accommodations,
    locations,
    today,
    prefill,
    initial,
    lockedAccommodationId,
    compact = false
}): ReactNode => {
    const { t, locale } = useI18n()
    const initialAccommodation = lockedAccommodationId ?? initial?.accommodationId ?? ''
    const initialLocation = accommodations.find(item => item.id === initialAccommodation)?.location

    const [form, setForm] = useState<FormState>({
        destination: initialLocation ?? (initial?.destination && locations.includes(initial.destination) ? initial.destination : ''),
        accommodationId: initialAccommodation,
        arrivalDate: initial?.arrival ?? '',
        departureDate: initial?.departure ?? '',
        adults: String(initial?.adults ?? 2),
        children: String(initial?.children ?? 0),
        firstName: prefill?.firstName ?? '',
        lastName: prefill?.lastName ?? '',
        email: prefill?.email ?? '',
        phone: prefill?.phone ?? '',
        nationality: prefill?.nationality ?? '',
        specialRequests: '',
        message: '',
        privacy: false,
        website: ''
    })

    const [errors, setErrors] = useState<Record<string, string>>({})
    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)
    const [success, setSuccess] = useState<SuccessState | null>(null)

    const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm(current => ({ ...current, [key]: value }))
        setErrors(current => {
            if (!current[key]) return current

            const next = { ...current }

            delete next[key]

            return next
        })
    }

    const visibleAccommodations = useMemo(
        () => accommodations.filter(item => !form.destination || form.destination === FLEXIBLE || item.location === form.destination),
        [accommodations, form.destination]
    )

    const selected = accommodations.find(item => item.id === form.accommodationId) ?? null
    const guests = (Number.parseInt(form.adults, 10) || 0) + (Number.parseInt(form.children, 10) || 0)
    const nights = form.arrivalDate && form.departureDate ? nightsBetween(form.arrivalDate, form.departureDate) : 0
    const overCapacity = Boolean(selected?.maxGuests && guests > selected.maxGuests)

    const errorText = (key: string): string | null => {
        const code = errors[key]

        if (!code) return null

        return (t.errors as Record<string, string>)[code] ?? t.errors.validation
    }

    const validate = (): Record<string, string> => {
        const result: Record<string, string> = {}

        if (!form.arrivalDate) result.arrivalDate = 'required'
        else if (form.arrivalDate < today) result.arrivalDate = 'date_past'

        if (!form.departureDate) result.departureDate = 'required'
        else if (form.arrivalDate && form.departureDate <= form.arrivalDate) result.departureDate = 'date_order'
        else if (nights > 90) result.departureDate = 'stay_too_long'

        const adults = Number.parseInt(form.adults, 10)
        const children = Number.parseInt(form.children, 10)

        if (!Number.isFinite(adults) || adults < 1 || adults > 30) result.adults = 'invalid_number'
        if (!Number.isFinite(children) || children < 0 || children > 30) result.children = 'invalid_number'
        if (!form.firstName.trim()) result.firstName = 'required'
        if (!form.lastName.trim()) result.lastName = 'required'
        if (!form.email.trim()) result.email = 'required'
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) result.email = 'invalid_email'
        if (!form.phone.trim()) result.phone = 'required'
        else if (!/^[+()\d][\d\s()./-]{5,28}$/.test(form.phone.trim())) result.phone = 'invalid_phone'
        if (!form.nationality) result.nationality = 'invalid_country'
        if (!form.privacy) result.privacy = 'consent_required'

        return result
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setFormError(null)

        const found = validate()

        setErrors(found)

        if (Object.keys(found).length > 0) {
            const first = Object.keys(found)[0]

            document.getElementById(`booking-${first}`)?.focus()

            return
        }

        setSubmitting(true)

        try {
            const result = await apiRequest<{ bookingNumber: string, customerEmailSent: boolean }>('/api/booking-requests', {
                method: 'POST',
                body: {
                    destination: form.destination === FLEXIBLE ? null : form.destination || null,
                    accommodationId: form.accommodationId && form.accommodationId !== ADVICE ? form.accommodationId : null,
                    arrivalDate: form.arrivalDate,
                    departureDate: form.departureDate,
                    adults: Number.parseInt(form.adults, 10),
                    children: Number.parseInt(form.children, 10),
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                    nationality: form.nationality,
                    specialRequests: form.specialRequests,
                    message: form.message,
                    privacyAccepted: form.privacy,
                    website: form.website,
                    locale
                }
            })

            setSuccess({ bookingNumber: result.bookingNumber, customerEmailSent: result.customerEmailSent, email: form.email.trim() })
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        catch (error) {
            const fields = errorFields(error)

            if (Object.keys(fields).length) setErrors(fields)

            const code = errorCode(error)

            setFormError((t.errors as Record<string, string>)[code] ?? t.errors.server_error)
        }
        finally {
            setSubmitting(false)
        }
    }

    if (success) {
        return (
            <div className='flex flex-col items-center gap-5 rounded-3xl border border-border bg-card px-6 py-12 text-center shadow-sm' role='status'>
                <span className='flex size-16 items-center justify-center rounded-full bg-success-soft text-success'>
                    <CheckCircle2 className='size-8' aria-hidden='true' />
                </span>
                <h2 className='text-3xl font-semibold text-navy'>{t.booking.successTitle}</h2>
                <p className='max-w-md text-muted-foreground'>{t.booking.successText}</p>
                <p className='rounded-2xl bg-sand px-6 py-3 font-mono text-2xl font-bold tracking-wider text-navy' dir='ltr'>{success.bookingNumber}</p>
                <p className='max-w-md text-sm text-muted-foreground'>
                    {success.customerEmailSent ? fill(t.booking.successEmail, { email: success.email }) : t.booking.successNoEmail}
                </p>
                <div className='mt-2 flex flex-wrap justify-center gap-3'>
                    {prefill
                        ? <Link href='/account/requests' className={buttonClass({ variant: 'primary' })}>{t.booking.successAccount}</Link>
                        : <Link href='/' className={buttonClass({ variant: 'primary' })}>{t.booking.successHome}</Link>}
                    <button
                        type='button'
                        className={buttonClass({ variant: 'outline' })}
                        onClick={() => {
                            setSuccess(null)
                            setForm(current => ({ ...current, specialRequests: '', message: '', privacy: false }))
                        }}
                    >
                        {t.booking.successNew}
                    </button>
                </div>
            </div>
        )
    }

    const sectionClass = compact ? 'flex flex-col gap-4' : 'rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8'
    const gridClass = compact ? 'grid gap-4' : 'grid gap-5 sm:grid-cols-2'

    return (
        <form onSubmit={submit} noValidate className='flex flex-col gap-6'>
            <div className={sectionClass}>
                {!compact && <h2 className='mb-5 font-sans text-lg font-bold text-navy'>{t.booking.sectionTravel}</h2>}

                <div className={gridClass}>
                    {!lockedAccommodationId && (
                        <>
                            <Field label={t.booking.destination} htmlFor='booking-destination'>
                                <Select
                                    id='booking-destination'
                                    value={form.destination}
                                    onChange={(event) => {
                                        const destination = event.target.value
                                        const keep = !form.accommodationId || form.accommodationId === ADVICE || destination === FLEXIBLE || !destination || accommodations.find(item => item.id === form.accommodationId)?.location === destination

                                        setForm(current => ({ ...current, destination, accommodationId: keep ? current.accommodationId : '' }))
                                    }}
                                >
                                    <option value=''>{t.common.choose}</option>
                                    {locations.map(location => <option key={location} value={location}>{location}</option>)}
                                    <option value={FLEXIBLE}>{t.booking.destinationFlexible}</option>
                                </Select>
                            </Field>

                            <Field label={t.booking.accommodation} htmlFor='booking-accommodationId'>
                                <Select
                                    id='booking-accommodationId'
                                    value={form.accommodationId}
                                    onChange={(event) => {
                                        const accommodationId = event.target.value
                                        const location = accommodations.find(item => item.id === accommodationId)?.location

                                        setForm(current => ({ ...current, accommodationId, destination: location ?? current.destination }))
                                    }}
                                >
                                    <option value=''>{t.common.choose}</option>
                                    {visibleAccommodations.map(item => (
                                        <option key={item.id} value={item.id}>{`${item.name} · ${t.types[item.type]} · ${item.location}`}</option>
                                    ))}
                                    <option value={ADVICE}>{t.booking.accommodationAdvice}</option>
                                </Select>
                            </Field>
                        </>
                    )}

                    <Field label={t.booking.arrival} htmlFor='booking-arrivalDate' error={errorText('arrivalDate')}>
                        <Input
                            id='booking-arrivalDate'
                            type='date'
                            min={today}
                            value={form.arrivalDate}
                            invalid={Boolean(errors.arrivalDate)}
                            required
                            onChange={(event) => {
                                const arrivalDate = event.target.value

                                update('arrivalDate', arrivalDate)

                                if (arrivalDate && (!form.departureDate || form.departureDate <= arrivalDate)) update('departureDate', addDays(arrivalDate, 7))
                            }}
                        />
                    </Field>

                    <Field label={t.booking.departure} htmlFor='booking-departureDate' error={errorText('departureDate')}>
                        <Input
                            id='booking-departureDate'
                            type='date'
                            min={form.arrivalDate ? addDays(form.arrivalDate, 1) : today}
                            value={form.departureDate}
                            invalid={Boolean(errors.departureDate)}
                            required
                            onChange={event => update('departureDate', event.target.value)}
                        />
                    </Field>

                    <Field label={t.booking.adults} htmlFor='booking-adults' error={errorText('adults')}>
                        <Input
                            id='booking-adults'
                            type='number'
                            inputMode='numeric'
                            min={1}
                            max={30}
                            value={form.adults}
                            invalid={Boolean(errors.adults)}
                            onChange={event => update('adults', event.target.value)}
                        />
                    </Field>

                    <Field label={t.booking.children} htmlFor='booking-children' error={errorText('children')}>
                        <Input
                            id='booking-children'
                            type='number'
                            inputMode='numeric'
                            min={0}
                            max={30}
                            value={form.children}
                            invalid={Boolean(errors.children)}
                            onChange={event => update('children', event.target.value)}
                        />
                    </Field>
                </div>

                {(nights > 0 || overCapacity) && (
                    <div className={cn('mt-5 flex flex-col gap-2 rounded-2xl bg-sand px-4 py-3 text-sm', compact && 'mt-1')}>
                        {nights > 0 && (
                            <p className='flex items-center gap-2 text-navy'>
                                <CalendarDays className='size-4 text-gold' aria-hidden='true' />
                                <span className='font-semibold'>{plural(nights, t.common.nightOne, t.common.nightOther)}</span>
                                <span className='text-muted-foreground'>·</span>
                                <Users className='size-4 text-gold' aria-hidden='true' />
                                <span>{plural(Number.parseInt(form.adults, 10) || 0, t.common.adultOne, t.common.adultOther)}</span>
                                {(Number.parseInt(form.children, 10) || 0) > 0 && <span>, {plural(Number.parseInt(form.children, 10) || 0, t.common.childOne, t.common.childOther)}</span>}
                            </p>
                        )}
                        {overCapacity && selected?.maxGuests && (
                            <p className='flex items-start gap-2 text-[oklch(0.45_0.11_65)]'>
                                <Info className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                                {fill(t.booking.capacityWarning, { count: selected.maxGuests })}
                            </p>
                        )}
                    </div>
                )}
            </div>

            <div className={sectionClass}>
                {!compact && <h2 className='mb-5 font-sans text-lg font-bold text-navy'>{t.booking.sectionGuest}</h2>}

                {prefill && (
                    <p className='mb-5 rounded-2xl bg-info-soft px-4 py-3 text-sm text-info'>
                        {fill(t.booking.loggedInAs, { name: `${prefill.firstName} ${prefill.lastName}` })}
                    </p>
                )}

                <div className={gridClass}>
                    <Field label={t.booking.firstName} htmlFor='booking-firstName' error={errorText('firstName')}>
                        <Input id='booking-firstName' autoComplete='given-name' value={form.firstName} invalid={Boolean(errors.firstName)} maxLength={80} onChange={event => update('firstName', event.target.value)} />
                    </Field>

                    <Field label={t.booking.lastName} htmlFor='booking-lastName' error={errorText('lastName')}>
                        <Input id='booking-lastName' autoComplete='family-name' value={form.lastName} invalid={Boolean(errors.lastName)} maxLength={80} onChange={event => update('lastName', event.target.value)} />
                    </Field>

                    <Field label={t.booking.email} htmlFor='booking-email' error={errorText('email')}>
                        <Input id='booking-email' type='email' autoComplete='email' value={form.email} invalid={Boolean(errors.email)} readOnly={Boolean(prefill)} maxLength={254} onChange={event => update('email', event.target.value)} />
                    </Field>

                    <Field label={t.booking.phone} htmlFor='booking-phone' error={errorText('phone')}>
                        <Input id='booking-phone' type='tel' autoComplete='tel' dir='ltr' value={form.phone} invalid={Boolean(errors.phone)} maxLength={30} onChange={event => update('phone', event.target.value)} />
                    </Field>

                    <Field label={t.booking.nationality} htmlFor='booking-nationality' error={errorText('nationality')} className={compact ? undefined : 'sm:col-span-2'}>
                        <CountrySelect
                            id='booking-nationality'
                            value={form.nationality}
                            onChange={value => update('nationality', value)}
                            locale={locale}
                            placeholder={t.common.choose}
                            commonLabel={t.common.commonCountries}
                            allLabel={t.common.allCountries}
                            invalid={Boolean(errors.nationality)}
                        />
                    </Field>
                </div>
            </div>

            <div className={sectionClass}>
                {!compact && <h2 className='mb-5 font-sans text-lg font-bold text-navy'>{t.booking.sectionWishes}</h2>}

                <div className='grid gap-5'>
                    <Field label={t.booking.specialRequests} htmlFor='booking-specialRequests' optional={t.common.optional} hint={t.booking.specialRequestsHint}>
                        <Textarea id='booking-specialRequests' rows={3} maxLength={2000} value={form.specialRequests} onChange={event => update('specialRequests', event.target.value)} />
                    </Field>

                    <Field label={t.booking.message} htmlFor='booking-message' optional={t.common.optional}>
                        <Textarea id='booking-message' rows={compact ? 3 : 4} maxLength={4000} value={form.message} onChange={event => update('message', event.target.value)} />
                    </Field>

                    <div aria-hidden='true' className='absolute -start-[9999px] h-px w-px overflow-hidden'>
                        <label htmlFor='booking-website'>Website</label>
                        <input id='booking-website' tabIndex={-1} autoComplete='off' value={form.website} onChange={event => update('website', event.target.value)} />
                    </div>

                    <div>
                        <Checkbox
                            id='booking-privacy'
                            checked={form.privacy}
                            invalid={Boolean(errors.privacy)}
                            onChange={event => update('privacy', event.target.checked)}
                            label={(
                                <>
                                    {t.booking.privacyBefore}
                                    <Link href='/privacy' target='_blank' className='font-semibold text-navy underline underline-offset-2'>{t.booking.privacyLink}</Link>
                                    {t.booking.privacyAfter}
                                </>
                            )}
                        />
                        {errors.privacy && <p role='alert' className='mt-1.5 text-xs font-medium text-destructive'>{errorText('privacy')}</p>}
                    </div>

                    {!prefill && !compact && (
                        <p className='text-sm text-muted-foreground'>
                            {t.booking.guestHintBefore}
                            <Link href='/register' className='font-semibold text-navy underline underline-offset-2'>{t.booking.guestHintLink}</Link>
                            {t.booking.guestHintAfter}
                        </p>
                    )}
                </div>
            </div>

            {formError && (
                <p role='alert' className='rounded-2xl bg-danger-soft px-4 py-3 text-sm font-medium text-destructive'>{formError}</p>
            )}

            <Button type='submit' variant='gold' size='lg' loading={submitting} className='w-full sm:w-auto sm:self-start'>
                {submitting ? t.booking.submitting : t.booking.submit}
            </Button>
        </form>
    )
}
